import { Care } from '.';
import { Fate } from '../fate';
import { Inventory } from '../inventory';
import { Pet, PetStatusEffect } from '../pet';
import { PetStats } from '../pet/stats';
import { CareItem } from './care.ruleset';

export const careModifiers = ({ care }: Care.MutableState) => ({
  incrementCareActionCount() {
    care.careActionCount += 1;
  },
  purchaseCareItem(state, careItemId: CareItem['id'], api) {
    const { ruleset } = Care;
    const item = ruleset.careItems[careItemId];
    const now = api.date.now();
    if (
      Inventory.getters(state).getPoints() < item.price ||
      !Care.getters(state).canPerformCareAction(item.type, now)
    ) {
      return { consumed: false };
    }

    const { getCurrencies } = Inventory.getters(state);
    const oldCurrencies = getCurrencies(now);
    Inventory.modifiers(state).consumePoints(item.price);

    // Send CurrencyGrant event
    api.sendAnalyticsEvents([
      Inventory.ruleset.getCurrencyGrantEvent(
        {
          feature: 'care_action',
          $subFeature: careItemId,
        },
        {
          old: oldCurrencies,
          new: getCurrencies(now),
        },
      ),
    ]);

    const {
      getAttributeValue,
      hasStatusEffect,
      getResolutionAttempts,
    } = Pet.getters(state);
    const { setAttribute, incrementResolutionAttempts } = PetStats.modifiers;
    const { resolveStatusEffect } = Fate.api;
    const { petAttributes, decayMultipliers, petStatusEffects } = Pet.ruleset;
    const { friendId, unsyncedChanges } = state.visit.visitSession ?? {};

    petAttributes.forEach((attribute) => {
      const amount = ruleset.getCareItemAmount(item, attribute);
      if (amount) {
        const currentValue = getAttributeValue(attribute, now);
        const multiplier = -decayMultipliers[attribute];
        const addedValue = amount * multiplier;
        setAttribute(state, attribute, currentValue + addedValue, now);
        // Should also update attribute of visited pet.
        if (friendId) {
          const current = unsyncedChanges.attributes[attribute] ?? 0;
          unsyncedChanges.attributes[attribute] = current + addedValue;
        }
      }
    });

    let resolvedStatusEffect: boolean = undefined;
    let targetStatusEffects: PetStatusEffect[] = undefined;
    petStatusEffects.forEach((statusEffect) => {
      let hasEffect = hasStatusEffect(statusEffect, now);

      const resolutionAttempts = getResolutionAttempts(statusEffect);
      const cureChance = ruleset.getCareItemCureChance(
        item,
        statusEffect,
        resolutionAttempts,
      );

      // Check if status effect should be resolved.
      if (cureChance > 0) {
        const shouldResolve = api.math.random() < cureChance;

        // Increment resolution attempts.
        if (hasEffect) {
          incrementResolutionAttempts(state, statusEffect);
          hasEffect = !shouldResolve;
          if (!targetStatusEffects) {
            targetStatusEffects = [];
          }
          targetStatusEffects.push(statusEffect);
          resolvedStatusEffect = resolvedStatusEffect || !hasEffect;
        }

        // Even if local pet doesn't have the effect, resolve it for friend's pet.
        if (friendId && shouldResolve) {
          unsyncedChanges.statusEffects[statusEffect] = false;
        }
      }

      resolveStatusEffect(state, statusEffect, hasEffect, now);
    });
    Fate.api.update(state, api.date.now());

    Care.modifiers(state).incrementCareActionCount();

    return { consumed: true, resolvedStatusEffect, targetStatusEffects };
  },
});
